/**
 * Toast — minimal context + provider + `useToast()` hook.
 *
 * Why hand-rolled:
 *   - The product brief explicitly forbids new dependencies for Phase 3.6.
 *   - We only need three levels (success / danger / info) and a 3-second
 *     auto-dismiss; pulling in `react-toastify` or `sonner` would add
 *     ~30KB gz for what fits in 100 lines.
 *
 * Visual:
 *   - Stacked at the bottom-right, newest first.
 *   - Each toast occupies its own card with the semantic-foreground colour
 *     plus a manual close button.
 *   - All design values come from existing tokens (`semantic.{success,
 *     danger,info}{,Bg}`, `z-toast`, `radius.md`, `shadow.md`,
 *     `transition.duration.normal`).
 *
 * Behaviour:
 *   - `show(message, level?)` enqueues a toast and starts a 3s timer.
 *   - Returning the toast id lets callers cancel early via `dismiss(id)`.
 *   - The provider cleans up timers on unmount so React Strict Mode mount
 *     /unmount cycles don't leak.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

export type ToastLevel = 'info' | 'success' | 'danger';

interface ToastEntry {
  id: number;
  message: string;
  level: ToastLevel;
}

interface ToastContextValue {
  show: (message: string, level?: ToastLevel) => number;
  dismiss: (id: number) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

const AUTO_DISMISS_MS = 3000;

export function ToastProvider({ children }: { children: ReactNode }): React.JSX.Element {
  const [toasts, setToasts] = useState<ToastEntry[]>([]);
  const timersRef = useRef<Map<number, number>>(new Map());
  const nextIdRef = useRef<number>(1);

  const dismiss = useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
    const handle = timersRef.current.get(id);
    if (handle !== undefined) {
      window.clearTimeout(handle);
      timersRef.current.delete(id);
    }
  }, []);

  const show = useCallback(
    (message: string, level: ToastLevel = 'info'): number => {
      const id = nextIdRef.current;
      nextIdRef.current = id + 1;
      setToasts((prev) => [...prev, { id, message, level }]);
      const handle = window.setTimeout(() => {
        dismiss(id);
      }, AUTO_DISMISS_MS);
      timersRef.current.set(id, handle);
      return id;
    },
    [dismiss],
  );

  // Clean up every pending timer when the provider itself unmounts. Doing
  // this avoids the stale-closure setState warning under React Strict Mode.
  useEffect(() => {
    const timers = timersRef.current;
    return () => {
      for (const handle of timers.values()) window.clearTimeout(handle);
      timers.clear();
    };
  }, []);

  const value = useMemo<ToastContextValue>(() => ({ show, dismiss }), [show, dismiss]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <ToastViewport toasts={toasts} onDismiss={dismiss} />
    </ToastContext.Provider>
  );
}

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    throw new Error('useToast() must be called inside a <ToastProvider>.');
  }
  return ctx;
}

interface ToastViewportProps {
  toasts: ToastEntry[];
  onDismiss: (id: number) => void;
}

function ToastViewport({ toasts, onDismiss }: ToastViewportProps): React.JSX.Element | null {
  if (toasts.length === 0) return null;

  return (
    <div
      role="region"
      aria-label="通知"
      aria-live="polite"
      className="pointer-events-none fixed bottom-6 right-6 z-toast flex w-80 flex-col gap-2"
    >
      {toasts.map((toast) => (
        <ToastCard key={toast.id} entry={toast} onDismiss={onDismiss} />
      ))}
    </div>
  );
}

interface ToastCardProps {
  entry: ToastEntry;
  onDismiss: (id: number) => void;
}

function ToastCard({ entry, onDismiss }: ToastCardProps): React.JSX.Element {
  const palette = {
    info: {
      bg: 'bg-semantic-infoBg',
      fg: 'text-semantic-info',
      border: 'border-semantic-info',
    },
    success: {
      bg: 'bg-semantic-successBg',
      fg: 'text-semantic-success',
      border: 'border-semantic-success',
    },
    danger: {
      bg: 'bg-semantic-dangerBg',
      fg: 'text-semantic-danger',
      border: 'border-semantic-danger',
    },
  }[entry.level];

  return (
    <div
      role="status"
      className={`pointer-events-auto flex items-start gap-3 rounded-md border ${palette.border} ${palette.bg} px-3 py-2 shadow-md transition-opacity duration-normal`}
    >
      <p className={`flex-1 text-xs ${palette.fg}`}>{entry.message}</p>
      <button
        type="button"
        onClick={() => onDismiss(entry.id)}
        aria-label="关闭通知"
        className={`-mr-1 -mt-0.5 rounded-sm px-1 text-xs ${palette.fg} hover:opacity-70`}
      >
        ✕
      </button>
    </div>
  );
}

export default ToastProvider;
